import { getProxyUrl } from '.'

export type TiendaApi = {
  idTiendas: number
  nombre: string
  direccion: string
  codigoPostal: string
  ciudad: string
  telefono: string
  correo: string
  cif: string
  razonSocial: string
  personaDeContacto: string
  pais: string
}

export type CreateTiendaData = {
  nombre: string
  direccion: string
  codigoPostal: string
  telefono: string
  correo: string
  razonSocial: string
  cif: string
  personaDeContacto: string
  ciudad: string
  pais: string
}

export async function getTiendas(): Promise<TiendaApi[]> {
  const token = sessionStorage.getItem('access_token')
  if (!token) {
    throw new Error('No hay token de acceso')
  }

  const response = await fetch(getProxyUrl('api/Tienda/getTiendas'), {
    headers: {
      'Accept-Language': 'ES',
      Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
    },
  })

  if (!response.ok) {
    throw new Error(`Error al obtener tiendas: ${response.status}`)
  }

  try {
    const data = await response.json()
    return data as TiendaApi[]
  } catch (error) {
    console.error('Error parsing response:', error)
    throw new Error('Error al procesar la respuesta del servidor')
  }
}

export async function createTienda(data: CreateTiendaData): Promise<TiendaApi> {
  const token = sessionStorage.getItem('access_token')
  if (!token) {
    throw new Error('No hay token de acceso')
  }

  const response = await fetch(getProxyUrl('api/Tienda/nuevaTienda'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'ES',
      Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
    },
    body: JSON.stringify(data),
  })

  // Si es un error 500, intentamos obtener la tienda recién creada
  if (response.status === 500 || response.status === 204) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const tiendas = await getTiendas()
      const nuevaTienda = tiendas.find((t) => t.nombre === data.nombre && t.correo === data.correo)
      if (nuevaTienda) {
        return nuevaTienda
      }
    } catch (error) {
      console.error('Error al recuperar la tienda creada:', error)
    }
  }

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `Error al crear la tienda: ${response.status}`)
  }

  // Si la respuesta es 204 o no tiene body, buscar la tienda creada
  const text = await response.text()
  if (!text) {
    const tiendas = await getTiendas()
    const nuevaTienda = tiendas.find((t) => t.nombre === data.nombre && t.correo === data.correo)
    if (nuevaTienda) {
      return nuevaTienda
    }
    throw new Error('Tienda creada pero no encontrada en la lista')
  }

  try {
    const newTienda = JSON.parse(text)
    return newTienda as TiendaApi
  } catch (error) {
    console.error('Error parsing response:', error)
    throw new Error('Error al procesar la respuesta del servidor')
  }
}

export async function modificarTienda(data: TiendaApi): Promise<TiendaApi> {
  const token = sessionStorage.getItem('access_token')
  if (!token) {
    throw new Error('No hay token de acceso')
  }

  const response = await fetch(getProxyUrl('api/Tienda/modificarTienda'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'ES',
      Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `Error al modificar la tienda: ${response.status}`)
  }

  // Si la respuesta es 204 o no tiene body, buscar la tienda modificada
  const text = await response.text()
  if (!text) {
    const tiendas = await getTiendas()
    const tiendaModificada = tiendas.find((t) => t.idTiendas === data.idTiendas)
    if (tiendaModificada) {
      return tiendaModificada
    }
    throw new Error('Tienda modificada pero no encontrada en la lista')
  }

  try {
    const updatedTienda = JSON.parse(text)
    return updatedTienda as TiendaApi
  } catch (error) {
    console.error('Error parsing response:', error)
    throw new Error('Error al procesar la respuesta del servidor')
  }
}
